import React, { useState } from "react";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { LogIn, LogOut, Loader2 } from "lucide-react";

/**
 * CheckInOut button for a booking — sitter-only. Shows Check In when not yet
 * checked in; Check Out when checked in but not yet out; and a completed pill
 * when both have occurred.
 */
export default function CheckInOut({ booking, myRole, onChange }) {
  const [busy, setBusy] = useState(false);
  if (myRole !== "sitter") {
    // family sees the timeline (read-only)
    const s = booking.session;
    if (!s?.checked_in_at) return null;
    return (
      <div className="mt-2 text-xs text-stone2-600" data-testid={`session-view-${booking.id}`}>
        Sitter checked in {new Date(s.checked_in_at).toLocaleString()}
        {s.checked_out_at ? ` · checked out ${new Date(s.checked_out_at).toLocaleString()} (${s.actual_hours || "—"}h actual)` : " · still on-shift"}
      </div>
    );
  }

  const call = async (path, evtLabel) => {
    setBusy(true);
    let lat = null, lng = null;
    try {
      if ("geolocation" in navigator) {
        await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (pos) => { lat = pos.coords.latitude; lng = pos.coords.longitude; resolve(); },
            () => resolve(),
            { timeout: 4000 },
          );
        });
      }
      await api.post(`/bookings/${booking.id}/${path}`, { lat, lng });
      toast.success(`${evtLabel} recorded`);
      onChange?.();
    } catch (e) { toast.error(extractError(e)); }
    finally { setBusy(false); }
  };

  const s = booking.session || {};

  if (!s.checked_in_at) {
    return (
      <button type="button" onClick={() => call("check-in", "Check-in")} disabled={busy}
        data-testid={`session-checkin-${booking.id}`}
        className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1.5 bg-sage-600 text-white hover:bg-sage-700 transition-colors duration-200">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogIn className="w-3.5 h-3.5" />} Check in
      </button>
    );
  }
  if (!s.checked_out_at) {
    return (
      <button type="button" onClick={() => call("check-out", "Check-out")} disabled={busy}
        data-testid={`session-checkout-${booking.id}`}
        className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1.5 bg-ochre-500 text-sage-900 hover:bg-ochre-600 hover:text-white transition-colors duration-200">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogOut className="w-3.5 h-3.5" />} Check out
      </button>
    );
  }
  return (
    <span className="chip" data-testid={`session-completed-${booking.id}`}>
      Session logged · {s.actual_hours || "—"}h actual
    </span>
  );
}
